import { useRole } from './useRole';
import { useUser } from './useUser';
import type { RoleType } from '@/lib/database.types';
import type { FeatureKey } from '@/lib/permissions';

const MODULE_KEYS = [
  'dashboard',
  'finances',
  'echeances',
  'crm',
  'formation',
  'marketing',
  'rh',
  'parametres',
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

const ACCESS_MATRIX: Record<RoleType, ModuleKey[]> = {
  super_admin: [...MODULE_KEYS],
  direction: ['dashboard', 'finances', 'echeances', 'crm', 'formation', 'marketing', 'rh'],
  responsable_activite: ['dashboard', 'crm', 'formation', 'echeances'],
  commercial: ['dashboard', 'crm', 'formation'],
  comptable: ['dashboard', 'finances', 'echeances'],
  formateur: ['formation'],
  responsable_rh: ['dashboard', 'rh'],
  responsable_marketing: ['dashboard', 'marketing', 'crm', 'finances'],
};

// Sprint 8.2 — Mapping module → feature_keys.view pour intégrer le whitelist
// JSON. Si une whitelist custom existe (users_metadata.permissions != null),
// canAccess('finances') = au moins une feature de ce module est dans la whitelist.
const MODULE_FEATURES: Record<ModuleKey, FeatureKey[]> = {
  dashboard: [], // toujours accessible
  finances: [
    'caisse.view',
    'tresorerie.view',
    'justificatifs.view',
    'cloture.view',
    'rapprochement.view',
    'comptes.view',
    'avances.view',
    'consolide_formateurs.view',
    'dettes_fournisseurs.view',
    'recouvrement.view',
  ],
  echeances: ['echeanciers.view'],
  crm: ['crm.view'],
  formation: ['formation.view'],
  marketing: ['marketing.view'],
  rh: [], // pas encore de feature_keys, on garde la matrice
  parametres: ['parametres.view'],
};

export function usePermissions(): {
  canAccess: (module: ModuleKey) => boolean;
  allowedModules: ModuleKey[];
  loading: boolean;
} {
  const { role, loading } = useRole();
  const { metadata } = useUser();
  const isAdmin = role === 'super_admin' || role === 'direction';
  const customPermissions =
    (metadata as unknown as { permissions?: Record<string, boolean> | null } | null)?.permissions ??
    null;

  const matrixAllowed = role ? ACCESS_MATRIX[role] : [];

  const canAccess = (module: ModuleKey): boolean => {
    if (isAdmin) return true;
    // Dashboard est toujours accessible aux users authenticated.
    if (module === 'dashboard') return true;
    // Whitelist custom : autoriser si au moins une feature .view est cochée.
    if (customPermissions) {
      const features = MODULE_FEATURES[module];
      return features.some((f) => customPermissions[f] === true);
    }
    // Comportement par défaut (matrice rôle).
    return matrixAllowed.includes(module);
  };

  const allowedModules = MODULE_KEYS.filter(canAccess);

  return {
    canAccess,
    allowedModules,
    loading,
  };
}
